var _ = require("lodash");

var Types = require("../Types");

/**
 * If this flag is set, constructors will check parameters
 *   with build-in criteria.
 * @type {Boolean}
 * @private
 */
var CHECK_PARAMETERS_IN_CONSTRUCTOR = true;

/**
 * check an object to be a lambda term.
 * @param {*} obj - any JS expression
 * @return `undefined` if `obj` is a lambda term.
 * @throws a string message if `obj` is not a lambda term.
 */
function checkLambdaTerm(obj) {
  if(isLambdaTerm(obj)) return;
  console.error(obj);
  throw "lambda term expected, but " + obj + " found";
}

/**
 * Lambda variable (untyped or typed).
 * @param {Type} [varType] - a type of the variable
 * @return {@link LambdaTerm}
 * @see sec. about types in lambda calculus and {@link Type}.
 */
function Var(varType) {
  if(!_.isEmpty(this)) return new Var(varType);
  if(varType !== undefined)
    this.varType = varType;
};

Var.prototype.type = function() { return this.varType; };



/**
 * An application of `func` to `arg`.
 * @param {LambdaTerm} func
 * @param {LambdaTerm} arg
 * @return {@link LambdaTerm}
 */
function Application(func, arg) {
  if(!_.isEmpty(this)) return new Application(func, arg);
  if(CHECK_PARAMETERS_IN_CONSTRUCTOR) {
    checkLambdaTerm(func);
    checkLambdaTerm(arg);
    if(func.objType == "List")
      throw "Do you seriously want to apply List?";
  }
  this.func = func;
  this.arg  = arg;
  // just for sure: derive a type
  try {
    Application.prototype.type.call(this);
  } catch(e) {
    console.error(this);
    throw e;
  }
}

Application.prototype.type = function() {
  return Types.ModusPonens(this.func.type(), this.arg.type());
}



/**
 * an abstraction.
 * @param {Var} arg {@link module:Lambda.Constructors~Var a variable} to bind,
 *   a formal argument
 * @param {LambdaTerm} body - an arbitrary lambda term
 * @return {@link LambdaTerm}
 */
function Lambda(arg, body) {
  if(!_.isEmpty(this)) return new Lambda(arg, body);
  if(CHECK_PARAMETERS_IN_CONSTRUCTOR) {
    checkLambdaTerm(body);
    if(!(arg instanceof Var)) {
      console.error(arg);
      throw "a formal argument of lambda abstract must be a variable";
    }
  }
  this.arg  = arg;
  this.body = body;
}

Lambda.prototype.type = function() {
  var argType = Types.simplify(this.arg.type());
  var bodyType = Types.simplify(this.body.type());
  return new Types.FunctionType(argType, bodyType);
};


/**
 * @summary
 * A collection of conditions.
 * Each condition is a function of External lambda term.
 * @private
 */
var checkExternal = {
  object: function(e) {
    if(e.object === undefined)
      console.warn("External: object of type " + e.objType + " is undefined");
  },
  func: function(e) {
    if(e.objType == "Function" &&
        ! _.isFunction(e.object) &&
        ! _.isString(e.object)) {
      console.error(e.object);
      throw "External: objType=\"Function\" but " +
          "an object is not a function nor a string";
    }
  },
  objType: function(e) {
    if(e.objType === undefined)
      throw "External: objType is undefined";
    if(! _.isString(e.objType) || e.objType == "")
      throw "External: objType is not a string";
  },
  termType: function(e) {
    if(e.objType == "Function" && e.termType === undefined) {
      console.warn(e.object);
      console.warn("External: Function has no termType. " +
          "It will cause inevitable bugs. Setting type to (? -> Object)");
      e.termType = new Types.FunctionType(Types.AnyType, "Object");
    }
  }
};



/**
 * @summary wrapper of custom JS object.
 *
 * @description
 * Each `External` object must be supplied with a string type [1]
 *   which is used by the Lambda interpreter to determine
 *   corresponding functions used by the interpreter.
 * A `termType` specifies a type of created lambda term.
 * If `termType` is ommited, a term's type is `objType` as a basic type.
 *
 * [1]: do not misread: a string type of External object `objType` and 
 *   a short form of {@link module:Types~BasicType a basic type}
 *   are different thinks.
 *   For example, an External term with `objType="Function"` is
 *   of {@link module:Types~FunctionType Functional type}.
 *
 * @param {*} object - unboxed object, arbitrary JS expression
 *   (`undefined` is ***strongly*** discouraged,
 *   other nil values, build-in objects and values of primitive type
 *   should not cause any trouble)
 * @param {string} objType - a string type of External object
 * @param {Type} [termType] - a type of a term, `BasicType(objType)` by default
 * @return {@link LambdaTerm}
 * @see {@link module:Lambda/Externals description of Externals}
 *   for more details about what `objType` is used for and how to introduce
 *   a new type of External objects.
 */
function External(object, objType, termType) {
  if(!_.isEmpty(this)) return new External(object, objType, termType);
  
  this.object  = object;
  this.objType = objType;
  this.termType = termType;
  
  if(CHECK_PARAMETERS_IN_CONSTRUCTOR) {
    checkExternal.object(this);
    checkExternal.objType(this);
    checkExternal.termType(this);
    checkExternal.func(this);
  }
}

External.prototype.type = function() {
  return this.termType || this.objType;
};

/**
 * Lambda term is an object representing a program of lambda calculus.
 *
 * There are 4 basic constructors of lambda terms:
 * - `Var`
 * - `Application`
 * - `Lambda`
 * - `External`
 *
 * First three constructors are common for all lambda calculi,
 * the last one is a way to use JS and external computing systems within
 * that lambda system.
 *
 * @typedef LambdaTerm
 * @global
 * @see module:Lambda/Constructors
 */

/**
 * Lambda term is JS object constructed with one of given constructors.
 * @param {*} obj - an JS expression
 * @return {boolean}
 */
function isLambdaTerm(obj) {
  return obj instanceof Var ||
         obj instanceof Application ||
         obj instanceof Lambda ||
         obj instanceof External;
}

/**
 * check whether given lambda term is an `External` lambda term.
 * If type is given, check also its type. That might prevent cases when
 *   an External lambda term of another type holds evaluation.
 * @param {LambdaTerm} term - a lambda term
 * @param {Type} type - a lambda type
 * @return `true` or `false`/`undefined`
 * @instance
 */
function isExternal(term, type) {
  if(!(term instanceof External)) return false;
  if(type === undefined) return true;
  if(_.isString(type)) return term.objType === type;
  return Types.compatible(type, term.termType);
}

/**
 * Lambda object is a wrapped lambda term.
 * It is an object with a field `type: "Lambda"`
 * and a field `term` which value is a lambda term.
 *
 * Lambda objects are used to highlight the fact that
 *   the term must be interpreted as lambda term using provided functions.
 * @typedef LambdaObject
 * @global
 * @see module:Lambda/Lambda-object
 */

/**
 * check whether `obj` is {@link module:Lambda/Lambda-object lambda object}.
 * @param {*} obj - any JS expression
 * @return {boolean}
 * @instance
 */
function isLambdaObject(obj) {
  return obj && obj.type == "Lambda" && obj.term;
}

module.exports = {
  Var: Var,
  Application: Application,
  Lambda: Lambda,
	External: External,
  isExternal: isExternal,
  isLambdaTerm: isLambdaTerm,
  isLambdaObject: isLambdaObject,
};
